import { useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { NodeValueSuggestionsField } from "@/components/doc/right-sidebar/active-node-style/fields/NodeValueSuggestionsField";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { getConvertUnitInfo, runConvertUnitAction } from "@/input/utils/convertUnitAction";

import type { SingleSuggestion } from "@/components/suggestions/types";
import type { ConvertCandidate } from "@/input/utils/convertUnitAction";
import type { NumberNodeModel } from "@/models/nodes/NumberNodeModel";

type Props = {
  node: NumberNodeModel;
};

export const ConvertField = observer(function ConvertField({ node }: Props) {
  const t = useTranslations("sidebar.activeNode");
  const [isBusy, setIsBusy] = useState(false);

  const info = getConvertUnitInfo(node);
  if (!info) {
    return null;
  }

  const commit = async (candidate: ConvertCandidate) => {
    setIsBusy(true);
    try {
      await runConvertUnitAction(node, candidate);
    } finally {
      setIsBusy(false);
    }
  };

  const handleSelect = (suggestion: SingleSuggestion) => {
    const candidate = info.candidates.find((c) => c.display === suggestion.item.value);
    if (candidate) {
      void commit(candidate);
    }
  };

  return (
    <>
      <Sidebar.Delimiter className="m-0" />
      <NodeValueSuggestionsField
        doc={node.doc}
        inputProps={{
          disabled: isBusy,
          name: t("fields.convertTo"),
          className: "shadow-none dark:shadow-none",
          labelProps: { label: t("fields.convertTo") },
          action: (
            <Dropdown
              toggleButtonLabel={t("unit.actions.convertTargets")}
              items={info.candidates.map((candidate) => ({
                title: candidate.unit.name ?? candidate.display,
                hotkey: (
                  <span className="font-mono text-xxs tracking-tight text-gray-500">
                    {candidate.display}
                  </span>
                ),
                onClick: () => {
                  void commit(candidate);
                },
              }))}
              toggleButtonComponent={
                <div className="flex items-center font-mono text-xxs uppercase tracking-tight text-gray-500">
                  <Icon name="ChevronDown" />
                </div>
              }
            />
          ),
        }}
        suggestionsProps={{
          accept: ["convertUnit"],
          providerSearchOptions: { convertUnit: { candidates: info.candidates } },
          onSelect: handleSelect,
        }}
      />
    </>
  );
});
